import React, {useEffect, useState} from 'react';
import {IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {CheckCircle, HelpOutline, Pending, PlayArrow, Report} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import api from '../../../api/axios';

import Lottie from 'lottie-react';
import loadingAnimation from '../../../assets/lottie/loading.json';
import JobConfirmModal from './modals/JobConfirmModal';

interface BatchJobDTO {
  id: number;
  jobName: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'NEVER_RUN';
  triggeredBy: 'SYSTEM' | 'USER';
  userId: number | null;
  startTime: string;
  endTime?: string;
  errorMessage?: string;
}

interface JobSummaryDTO {
  jobName: string;
  runningInstance: BatchJobDTO | null;
  lastSuccess: BatchJobDTO | null;
  lastFailure: BatchJobDTO | null;
}

const Jobs = () => {
  const {t} = useTranslation();
  const [summaries, setSummaries] = useState<JobSummaryDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Counter for aggressive polling after manual start
  const [shortPollCount, setShortPollCount] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);

  const fetchSummaries = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get('/batch-job/summary');
      setSummaries(response.data);

      // Reduce short poll counter on every successful fetch
      setShortPollCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Fehler beim Laden der Job-Summaries:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaries(true);
  }, []);

  useEffect(() => {
    const hasRunningJobs = summaries.some(s => s.runningInstance !== null);

    // Use 3s if jobs are running OR we are in short-poll phase, else 10s
    const intervalTime = (hasRunningJobs || shortPollCount > 0) ? 3000 : 10000;

    const interval = setInterval(() => fetchSummaries(false), intervalTime);
    return () => clearInterval(interval);
  }, [summaries, shortPollCount]);

  const handleOpenConfirm = (jobName: string) => {
    setSelectedJob(jobName);
    setConfirmOpen(true);
  };

  const handleTriggerJob = async () => {
    if (!selectedJob) return;

    const jobToRun = selectedJob;
    setConfirmOpen(false);
    setActionLoading(jobToRun);

    // Set counter to e.g. 3 to fetch aggressively for the next ~9 seconds
    setShortPollCount(3);

    try {
      await api.post(`/batch-job/${jobToRun.toLowerCase()}/run`);

      // Optimistic Update: Move to running locally
      setSummaries(prev => prev.map(s => {
        if (s.jobName === jobToRun) {
          return {
            ...s,
            runningInstance: {
              id: 0,
              jobName: s.jobName,
              status: 'RUNNING',
              triggeredBy: 'USER',
              userId: null,
              startTime: new Date().toISOString()
            }
          };
        }
        return s;
      }));

      // Small initial delay to let DB commit the new record
      setTimeout(() => fetchSummaries(false), 1000);
    } catch (error) {
      console.error('Fehler beim Starten des Jobs:', error);
      setShortPollCount(0);
    } finally {
      setActionLoading(null);
      setSelectedJob(null);
    }
  };

  const runningJobs = summaries.filter(s => s.runningInstance !== null);
  const failedJobs = summaries.filter(s => !s.runningInstance && s.lastFailure && (!s.lastSuccess || new Date(s.lastFailure.startTime) > new Date(s.lastSuccess.startTime)));
  const completedJobs = summaries.filter(s => !s.runningInstance && s.lastSuccess && (!s.lastFailure || new Date(s.lastSuccess.startTime) >= new Date(s.lastFailure.startTime)));
  const neverRunJobs = summaries.filter(s => !s.runningInstance && !s.lastSuccess && !s.lastFailure);

  const JobTable = ({title, icon, data, colorClass}: { title: string, icon: React.ReactNode, data: JobSummaryDTO[], colorClass: string }) => (
      <div className="mb-8">
        <div className={`flex items-center gap-2 mb-2 ${colorClass}`}>
          {icon}
          <Typography variant="h6" className="font-bold uppercase tracking-wider">
            {title} ({data.length})
          </Typography>
        </div>
        <TableContainer component={Paper} className="bg-light dark:bg-dark shadow-md rounded-lg overflow-hidden">
          <Table size="small" sx={{tableLayout: 'fixed'}}>
            <TableHead className="bg-gray-50 dark:bg-neutral-800">
              <TableRow>
                <TableCell className="font-bold" sx={{width: '30%'}}>Job Name</TableCell>
                <TableCell sx={{width: '60%'}}>Details</TableCell>
                <TableCell align="right" sx={{width: '10%'}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="left" className="text-gray-400 py-6 italic text-sm">
                      No jobs in this category
                    </TableCell>
                  </TableRow>
              ) : (
                  data.map((summary) => (
                      <TableRow key={summary.jobName} className="hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors">
                        <TableCell className="font-semibold text-indigo-600 dark:text-indigo-400 truncate">
                          {summary.jobName}
                        </TableCell>
                        <TableCell>
                          <div className="min-h-[40px] flex flex-col justify-center">
                            {summary.runningInstance && (
                                <div className="text-xs text-blue-500 animate-pulse font-bold">
                                  Started: {new Date(summary.runningInstance.startTime).toLocaleString('de-DE')}
                                </div>
                            )}
                            {title === 'Failed' && summary.lastFailure && (
                                <div className="w-full">
                                  <div className="text-[10px] text-red-400 font-mono">
                                    {new Date(summary.lastFailure.startTime).toLocaleString('de-DE')}
                                  </div>
                                  <Tooltip title={summary.lastFailure.errorMessage}>
                                    <div className="text-[11px] bg-red-50 dark:bg-red-900/10 p-1 mt-1 rounded border border-red-100 dark:border-red-900/30 text-red-700 dark:text-red-400 line-clamp-1 font-mono">
                                      {summary.lastFailure.errorMessage}
                                    </div>
                                  </Tooltip>
                                </div>
                            )}
                            {title === 'Completed' && summary.lastSuccess && (
                                <div className="text-xs text-gray-500">
                                  Last Success: {new Date(summary.lastSuccess.startTime).toLocaleString('de-DE')}
                                  <span className="ml-2 text-[10px] opacity-70">by {summary.lastSuccess.triggeredBy}</span>
                                </div>
                            )}
                            {title === 'Never Run' && <span className="text-xs text-gray-400 italic">Never executed yet</span>}
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Run Job Now">
                            <span>
                              <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenConfirm(summary.jobName)}
                                  disabled={!!summary.runningInstance || actionLoading === summary.jobName}
                              >
                                <PlayArrow fontSize="small"/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
  );

  return (
      <div className="p-4 h-[88vh] flex flex-col">
        <div className="mb-6 flex justify-between items-center shrink-0">
          <h1 className="text-2xl font-bold dark:text-white">Batch Job Monitoring</h1>
        </div>

        {loading && summaries.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <Lottie animationData={loadingAnimation} loop className="w-64"/>
            </div>
        ) : (
            <div className="overflow-y-auto pr-1 space-y-2 mt-2 scrollbar-thin scrollbar-thumb-gray-700">
              <JobTable title="Running" icon={<Pending className="animate-spin-slow"/>} data={runningJobs} colorClass="text-blue-500" />
              <JobTable title="Completed" icon={<CheckCircle/>} data={completedJobs} colorClass="text-green-600" />
              <JobTable title="Failed" icon={<Report/>} data={failedJobs} colorClass="text-red-500" />
              <JobTable title="Never Run" icon={<HelpOutline/>} data={neverRunJobs} colorClass="text-gray-400" />
            </div>
        )}

        <JobConfirmModal
            open={confirmOpen}
            jobName={selectedJob}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleTriggerJob}
        />
      </div>
  );
};

export default Jobs;